import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Check, Copy, MessageCircle, Upload, FileText, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { centerToast } from '../components/CenterToast';

const BASE_STEPS = [
  {
    id: 'received',
    title: 'Application Received',
    description: 'Your request is registered in our system.'
  },
  {
    id: 'documents',
    title: 'Documents Required',
    description: 'Please submit the requested verification documents.'
  },
  {
    id: 'review',
    title: 'Under HQ Review',
    description: 'Assessor is evaluating your application.'
  },
  {
    id: 'approved',
    title: 'Assistance Approved',
    description: 'Funds or items are being processed for disbursement.'
  }
];

export default function AssistanceTracker({ applicant, trackingLink, onBack }) {
  const fileInputRef = useRef(null);
  const [activeStepId, setActiveStepId] = useState('documents');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [docsSubmittedAt, setDocsSubmittedAt] = useState(null);

  const applicantName = applicant?.fullName || 'Siti Aminah';
  const assistanceType = applicant?.assistanceType || 'Housing';
  const submittedAt = applicant?.submittedAt || new Date('2026-04-05T15:02:00+08:00');
  const dateSubmittedLabel = submittedAt.toLocaleDateString('en-SG', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
  const step1Timestamp = submittedAt.toLocaleString('en-SG', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  });
  const step2Timestamp = docsSubmittedAt
    ? docsSubmittedAt.toLocaleString('en-SG', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
      })
    : null;

  // Derive status per step: everything before activeStepId is completed, one is active, rest pending
  const activeIdx = BASE_STEPS.findIndex((s) => s.id === activeStepId);
  const getStatus = (idx) => {
    if (idx < activeIdx) return 'completed';
    if (idx === activeIdx) return 'active';
    return 'pending';
  };

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    const combined = [...uploadedFiles, ...picked].slice(0, 5);
    setUploadedFiles(combined);
  };

  const removeFile = (idx) => setUploadedFiles((f) => f.filter((_, i) => i !== idx));

  const handleSubmitDocs = () => {
    if (uploadedFiles.length === 0) {
      toast.error('Please add at least one document', { position: 'top-center' });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDocsSubmittedAt(new Date());
      setActiveStepId('review');
      centerToast('Documents submitted. HQ Review has started.', '📨');
    }, 900);
  };

  const handleCopy = () => {
    if (!trackingLink) return;
    navigator.clipboard.writeText(trackingLink);
    centerToast('Tracking link copied to clipboard', '🔗');
  };

  const handleShare = () => {
    const text = encodeURIComponent(
      `Assalamualaikum,\n\nI'm tracking my Financial Assistance application under IhsanOne. View live status here:\n\n${trackingLink}\n\nMay Allah grant ease.`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-muted/20 pb-8">
      <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="assist-tracker-back">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-lg sm:text-xl font-semibold">Application Tracker</h1>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Live Timeline Section */}
        <section data-testid="live-timeline">
          <div className="flex items-center gap-3 mb-5">
            <h2 className="text-xl sm:text-2xl font-semibold">Live Timeline</h2>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold tracking-wider">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              ACTIVE
            </span>
          </div>

          <Card>
            <CardContent className="p-5 sm:p-6">
              <ol className="relative">
                {BASE_STEPS.map((step, idx) => {
                  const status = getStatus(idx);
                  const isLast = idx === BASE_STEPS.length - 1;
                  const isDocumentsActive = step.id === 'documents' && status === 'active';
                  const timestamp =
                    step.id === 'received' ? step1Timestamp :
                    step.id === 'documents' && status === 'completed' ? step2Timestamp : null;

                  return (
                    <li
                      key={step.id}
                      data-testid={`step-${step.id}`}
                      className="relative flex gap-4 pb-7 last:pb-0"
                    >
                      {!isLast && (
                        <span
                          aria-hidden="true"
                          className={`absolute left-[19px] top-10 -bottom-1 w-0.5 ${
                            status === 'completed' ? 'bg-primary' : 'bg-border'
                          }`}
                        />
                      )}

                      {/* Status circle */}
                      <div className="relative z-10 flex-shrink-0">
                        {status === 'completed' ? (
                          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm shadow-primary/30">
                            <Check className="h-5 w-5" strokeWidth={3} />
                          </div>
                        ) : status === 'active' ? (
                          <div className="relative w-10 h-10 rounded-full bg-primary/15 border-2 border-primary flex items-center justify-center">
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                            </span>
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full border-2 border-border bg-muted/40 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                          </div>
                        )}
                      </div>

                      {/* Step content */}
                      <div className="flex-1 pt-0.5 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <h3 className={`text-base font-semibold leading-tight ${
                            status === 'pending' ? 'text-muted-foreground/80' : 'text-foreground'
                          }`}>
                            {step.title}
                          </h3>
                          {status === 'active' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-wider">
                              IN PROGRESS
                            </span>
                          )}
                          {timestamp && (
                            <time className="text-xs text-muted-foreground whitespace-nowrap">
                              {timestamp}
                            </time>
                          )}
                        </div>
                        <p className={`text-sm mt-1 leading-relaxed ${
                          status === 'pending' ? 'text-muted-foreground/70' : 'text-muted-foreground'
                        }`}>
                          {step.description}
                        </p>

                        {/* Inline Upload CTA for active Documents step */}
                        {isDocumentsActive && (
                          <div className="mt-4 space-y-3" data-testid="inline-upload">
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept="image/png,image/jpeg,application/pdf"
                              onChange={handleFilePick}
                              className="hidden"
                              data-testid="tracker-file-input"
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              data-testid="tracker-dropzone"
                              className="w-full border-2 border-dashed border-[#5EEAD4] rounded-xl p-5 text-center hover:bg-[#5EEAD4]/10 transition-colors"
                            >
                              <Upload className="h-5 w-5 text-primary mx-auto mb-2" />
                              <p className="text-sm font-medium text-foreground">Tap here to upload documents</p>
                              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                                Max 5 files · PNG, JPG, PDF · Bills, pay-slips, doctor memos, ICs
                              </p>
                            </button>

                            {uploadedFiles.length > 0 && (
                              <ul className="space-y-1.5" data-testid="tracker-file-list">
                                {uploadedFiles.map((f, i) => (
                                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 bg-muted/40 rounded-md text-sm">
                                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                    <span className="flex-1 truncate">{f.name}</span>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(i)}
                                      className="text-muted-foreground hover:text-destructive"
                                      aria-label="Remove file"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}

                            <Button
                              data-testid="tracker-submit-docs"
                              onClick={handleSubmitDocs}
                              disabled={submitting || uploadedFiles.length === 0}
                              size="lg"
                              className="w-full h-11 text-sm gap-2"
                            >
                              {submitting ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Submitting…
                                </>
                              ) : (
                                `Submit ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'document' : 'documents'}`
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>
        </section>

        {/* Details View Section */}
        <section data-testid="details-view">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">Details View</h2>
          <Card className="bg-[#5EEAD4]/10 border-[#5EEAD4]/40">
            <CardContent className="p-5 sm:p-6">
              <dl className="divide-y divide-border/60">
                <div className="flex items-center justify-between py-3 first:pt-0">
                  <dt className="text-sm text-muted-foreground">Applicant</dt>
                  <dd className="text-sm font-semibold text-foreground text-right">{applicantName}</dd>
                </div>
                <div className="flex items-center justify-between py-3">
                  <dt className="text-sm text-muted-foreground">Assistance Type</dt>
                  <dd className="text-sm font-semibold text-foreground text-right">{assistanceType}</dd>
                </div>
                <div className="flex items-center justify-between py-3 last:pb-0">
                  <dt className="text-sm text-muted-foreground">Date Submitted</dt>
                  <dd className="text-sm font-semibold text-foreground text-right">{dateSubmittedLabel}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </section>

        {/* Share actions */}
        {trackingLink && (
          <section className="pt-2">
            <code
              data-testid="assist-tracker-link"
              className="text-[11px] sm:text-xs bg-muted/60 px-3 py-2 rounded border block break-all text-muted-foreground mb-3"
            >
              {trackingLink}
            </code>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                data-testid="assist-tracker-share"
                onClick={handleShare}
                size="lg"
                className="flex-1 h-11 text-sm gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Share via WhatsApp
              </Button>
              <Button
                data-testid="assist-tracker-copy"
                onClick={handleCopy}
                variant="outline"
                size="lg"
                className="flex-1 h-11 text-sm gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
